import { z } from "zod";

export const BATTLE_ROYALE_MIN_PLAYERS = 3;
// Enforced on input only (room requests, blueprint and campaign saves): a stored room above it still loads.
export const BATTLE_ROYALE_MAX_PLAYERS = 200;
export const BATTLE_ROYALE_INTERVAL_MIN_SECONDS = 10;
export const BATTLE_ROYALE_INTERVAL_MAX_SECONDS = 1800;
// Suggestions only: a private room accepts any whole number of seconds in the range.
export const BATTLE_ROYALE_PRIVATE_INTERVAL_PRESETS_SECONDS = [10, 30, 60, 120, 300] as const;

export const isBattleRoyaleIntervalSeconds = (seconds: number): boolean =>
  Number.isInteger(seconds) &&
  seconds >= BATTLE_ROYALE_INTERVAL_MIN_SECONDS &&
  seconds <= BATTLE_ROYALE_INTERVAL_MAX_SECONDS;

// Keeps a number and its unit on one line.
const NBSP = "\u00a0";

/** An interval or a game length: "45 s", "2 min", "1 min 30 s", "1 h 5 min"; zero units are dropped. */
export function formatBattleRoyaleDuration(totalSeconds: number): string {
  if (!Number.isInteger(totalSeconds) || totalSeconds < 0) {
    throw new RangeError(`A Battle Royale duration is a whole number of seconds, got ${totalSeconds}`);
  }
  if (totalSeconds === 0) return `0${NBSP}s`;
  const parts: Array<[number, string]> = [
    [Math.floor(totalSeconds / 3600), "h"],
    [Math.floor((totalSeconds % 3600) / 60), "min"],
    [totalSeconds % 60, "s"],
  ];
  return parts
    .filter(([value]) => value > 0)
    .map(([value, unit]) => `${value}${NBSP}${unit}`)
    .join(" ");
}
// Stored epoch end = schedule end + grace, so the last elimination's early finish settles, not the epoch end.
export const BATTLE_ROYALE_END_GRACE_MS = 60_000;

export const BATTLE_ROYALE_ELIMINATION_MODES = {
  ONE: "ONE",
  COUNT: "COUNT",
  PERCENT: "PERCENT",
  SCHEDULE: "SCHEDULE",
  ROUNDS: "ROUNDS",
} as const;
export type BattleRoyaleEliminationMode =
  (typeof BATTLE_ROYALE_ELIMINATION_MODES)[keyof typeof BATTLE_ROYALE_ELIMINATION_MODES];

export const BATTLE_ROYALE_COUNT_RANGE = { min: 1, max: 20 } as const;
export const BATTLE_ROYALE_PERCENT_RANGE = { min: 5, max: 50 } as const;
export const BATTLE_ROYALE_SCHEDULE_PERCENT_RANGE = { min: 5, max: 90 } as const;
export const BATTLE_ROYALE_SCHEDULE_MAX_STEPS = 10;
export const BATTLE_ROYALE_ROUNDS_RANGE = { min: 2, max: 60 } as const;

const intIn = (range: { min: number; max: number }) => z.number().int().min(range.min).max(range.max);

const ONE_RULE = { mode: z.literal(BATTLE_ROYALE_ELIMINATION_MODES.ONE) };
const COUNT_RULE = { mode: z.literal(BATTLE_ROYALE_ELIMINATION_MODES.COUNT), count: intIn(BATTLE_ROYALE_COUNT_RANGE) };
const PERCENT_RULE = {
  mode: z.literal(BATTLE_ROYALE_ELIMINATION_MODES.PERCENT),
  percent: intIn(BATTLE_ROYALE_PERCENT_RANGE),
};
const SCHEDULE_RULE = {
  mode: z.literal(BATTLE_ROYALE_ELIMINATION_MODES.SCHEDULE),
  // Round k uses percents[k - 1]; the last one repeats.
  percents: z.array(intIn(BATTLE_ROYALE_SCHEDULE_PERCENT_RANGE)).min(1).max(BATTLE_ROYALE_SCHEDULE_MAX_STEPS),
};
const ROUNDS_RULE = {
  mode: z.literal(BATTLE_ROYALE_ELIMINATION_MODES.ROUNDS),
  rounds: intIn(BATTLE_ROYALE_ROUNDS_RANGE),
};

/** How many players go out at each elimination. Percentages are of the players still in, rounded up. */
export const BattleRoyaleEliminationRuleSchema = z.discriminatedUnion("mode", [
  z.strictObject(ONE_RULE),
  z.strictObject(COUNT_RULE),
  z.strictObject(PERCENT_RULE),
  z.strictObject(SCHEDULE_RULE),
  z.strictObject(ROUNDS_RULE),
]);
/** For clients: strips a key a newer API added instead of throwing; an invalid value still throws. */
export const BattleRoyaleEliminationRuleReadSchema = z.discriminatedUnion("mode", [
  z.object(ONE_RULE),
  z.object(COUNT_RULE),
  z.object(PERCENT_RULE),
  z.object(SCHEDULE_RULE),
  z.object(ROUNDS_RULE),
]);
export type BattleRoyaleEliminationRule = z.infer<typeof BattleRoyaleEliminationRuleSchema>;

export const BATTLE_ROYALE_ONE_PER_ROUND: BattleRoyaleEliminationRule = { mode: BATTLE_ROYALE_ELIMINATION_MODES.ONE };

/** `finalDuel`: no elimination takes 3+ players straight to 1, so the last one is always a 1v1. */
export type BattleRoyaleCutRule = { elimination: BattleRoyaleEliminationRule; finalDuel: boolean };

const percentOf = (players: number, percent: number): number => Math.ceil((players * percent) / 100 - 1e-9);

/** Players the rule asks to cut at elimination `index` (1-based) from `playersIn`, before the caps. */
function requestedCut({ elimination: rule, finalDuel }: BattleRoyaleCutRule, playersIn: number, index: number): number {
  switch (rule.mode) {
    case BATTLE_ROYALE_ELIMINATION_MODES.ONE:
      return 1;
    case BATTLE_ROYALE_ELIMINATION_MODES.COUNT:
      return rule.count;
    case BATTLE_ROYALE_ELIMINATION_MODES.PERCENT:
      return percentOf(playersIn, rule.percent);
    case BATTLE_ROYALE_ELIMINATION_MODES.SCHEDULE:
      return percentOf(playersIn, rule.percents[Math.min(index, rule.percents.length) - 1]!);
    case BATTLE_ROYALE_ELIMINATION_MODES.ROUNDS: {
      const roundsLeft = rule.rounds - index + 1;
      if (roundsLeft <= 1) return playersIn - 1;
      // Geometric decay that reaches the final 1v1 (or the last player) on time, at least one cut per round left.
      const target = finalDuel
        ? Math.round(2 * Math.pow(playersIn / 2, (roundsLeft - 2) / (roundsLeft - 1)))
        : Math.round(Math.pow(playersIn, (roundsLeft - 1) / roundsLeft));
      return playersIn - Math.min(playersIn - 1, Math.max(roundsLeft, target));
    }
  }
}

/** Most players an elimination may cut: always leave one, and two while `finalDuel` holds the 1v1 for last. */
const maxCut = (rule: BattleRoyaleCutRule, playersIn: number): number =>
  rule.finalDuel && playersIn > 2 ? playersIn - 2 : playersIn - 1;

export type BattleRoyaleCut = { idleOut: number; tradersOut: number };

/**
 * Who elimination `index` cuts from `playersIn`, `idle` of whom never traded (only read at elimination 1).
 * Every idle player goes out at elimination 1 and counts toward its size; traders fill the rest, never
 * below one trader, nor below two while `finalDuel` holds and two or more traded.
 */
export function battleRoyaleCut(
  rule: BattleRoyaleCutRule,
  input: { index: number; playersIn: number; idle: number },
): BattleRoyaleCut {
  const { index, playersIn } = input;
  if (playersIn < 2) return { idleOut: 0, tradersOut: 0 };
  const size = Math.min(Math.max(1, requestedCut(rule, playersIn, index)), maxCut(rule, playersIn));
  const idleOut = index === 1 ? Math.min(input.idle, playersIn) : 0;
  if (idleOut === 0) return { idleOut, tradersOut: size };
  const traders = playersIn - idleOut;
  const tradersCap = Math.max(0, rule.finalDuel && traders >= 2 ? traders - 2 : traders - 1);
  return { idleOut, tradersOut: Math.min(Math.max(0, size - idleOut), tradersCap) };
}

/** Players cut at each remaining elimination, from elimination `fromIndex` with `playersIn` still in, nobody idle. */
export function planBattleRoyaleCuts(rule: BattleRoyaleCutRule, playersIn: number, fromIndex = 1): number[] {
  const cuts: number[] = [];
  for (let index = fromIndex, left = playersIn; left > 1; index++) {
    const { tradersOut } = battleRoyaleCut(rule, { index, playersIn: left, idle: 0 });
    cuts.push(tradersOut);
    left -= tradersOut;
  }
  return cuts;
}

/** Eliminations a game of `playerCount` plays when everyone trades. */
export const battleRoyaleRoundCount = (rule: BattleRoyaleCutRule, playerCount: number): number =>
  planBattleRoyaleCuts(rule, playerCount).length;

/** Seconds between eliminations; `first` is the warm-up before elimination 1, `final` the 1v1's length. Empty = the interval. */
export type BattleRoyaleTiming = {
  eliminationIntervalSeconds: number;
  firstEliminationSeconds?: number | null;
  finalEliminationSeconds?: number | null;
};

export type BattleRoyaleRule = BattleRoyaleCutRule & BattleRoyaleTiming;

export const battleRoyaleWarmUpSeconds = (timing: BattleRoyaleTiming): number =>
  timing.firstEliminationSeconds ?? timing.eliminationIntervalSeconds;

export const battleRoyaleFinalSeconds = (timing: BattleRoyaleTiming): number =>
  timing.finalEliminationSeconds ?? timing.eliminationIntervalSeconds;

/**
 * ONE mode keeps the planned length: an elimination that puts k players out (the idle sweep) skips the
 * next k - 1. Every other mode re-plans from the players still in, so the game ends sooner.
 */
export const battleRoyaleKeepsPlannedLength = (rule: BattleRoyaleCutRule): boolean =>
  rule.elimination.mode === BATTLE_ROYALE_ELIMINATION_MODES.ONE;

export type BattleRoyalePlannedElimination = {
  /** 1-based. */
  index: number;
  atMs: number;
  /** Players still in before it. */
  playersIn: number;
  out: number;
  /** Fought by the last two players; after elimination 1 it runs the final's length. */
  isFinal: boolean;
  /** Consumed by an earlier elimination that put several out (ONE mode): nobody goes out. */
  skipped: boolean;
};

/** Players each run elimination put out from elimination 1 on (0 for a skipped one), or the idle count elimination 1 will sweep. */
export type BattleRoyaleGameFacts = { recordedOut: ReadonlyArray<number> } | { idle: number };

function assertBattleRoyalePlayerCount(playerCount: number): void {
  if (!Number.isInteger(playerCount) || playerCount < 2) {
    throw new RangeError(`A Battle Royale needs at least 2 players, got ${playerCount}`);
  }
}

/** Seconds from the previous elimination (or the start) to the one at `position`, with `playersIn` still in. */
const runUpSeconds = (timing: BattleRoyaleTiming, position: number, playersIn: number): number =>
  position === 0
    ? battleRoyaleWarmUpSeconds(timing)
    : playersIn === 2
      ? battleRoyaleFinalSeconds(timing)
      : timing.eliminationIntervalSeconds;

function firstEliminationOut(rule: BattleRoyaleCutRule, playerCount: number, idle: number): number {
  if (!Number.isInteger(idle) || idle < 0 || idle > playerCount) {
    throw new RangeError(`Idle players must be a whole number from 0 to ${playerCount}, got ${idle}`);
  }
  const { idleOut, tradersOut } = battleRoyaleCut(rule, { index: 1, playersIn: playerCount, idle });
  return idleOut + tradersOut;
}

function assertRecordedOut(out: number, index: number, playersIn: number, skipped: boolean): void {
  const valid = Number.isInteger(out) && out <= playersIn && (skipped ? out === 0 : out >= 1);
  if (!valid) {
    throw new RangeError(
      `Battle Royale elimination ${index} recorded ${out} out of ${playersIn}, ${skipped ? "a skipped one puts nobody out" : "it must put at least one out"}`,
    );
  }
}

/**
 * Every elimination of a game of `playerCount`, from the facts known so far, until at most one player is left.
 * ONE mode keeps the full game's times and marks the skipped eliminations; other modes re-plan the rest.
 */
export function planBattleRoyaleGame(
  rule: BattleRoyaleRule,
  game: { startMs: number; playerCount: number } & BattleRoyaleGameFacts,
): BattleRoyalePlannedElimination[] {
  const { startMs, playerCount } = game;
  assertBattleRoyalePlayerCount(playerCount);
  const recordedOut = "recordedOut" in game ? game.recordedOut : [firstEliminationOut(rule, playerCount, game.idle)];
  const keepsLength = battleRoyaleKeepsPlannedLength(rule);
  const fullGameTimesMs: number[] = [];
  if (keepsLength) {
    let atMs = startMs;
    let playersIn = playerCount;
    planBattleRoyaleCuts(rule, playerCount).forEach((out, position) => {
      atMs += runUpSeconds(rule, position, playersIn) * 1000;
      fullGameTimesMs.push(atMs);
      playersIn -= out;
    });
  }
  const plan: BattleRoyalePlannedElimination[] = [];
  let atMs = startMs;
  let playersIn = playerCount;
  let skipsOwed = 0;
  for (let index = 1; playersIn >= 2; index++) {
    const skipped = skipsOwed > 0;
    const recorded = recordedOut[index - 1];
    if (recorded !== undefined) assertRecordedOut(recorded, index, playersIn, skipped);
    const out = recorded ?? (skipped ? 0 : battleRoyaleCut(rule, { index, playersIn, idle: 0 }).tradersOut);
    atMs = keepsLength ? fullGameTimesMs[index - 1]! : atMs + runUpSeconds(rule, index - 1, playersIn) * 1000;
    plan.push({ index, atMs, playersIn, out, isFinal: !skipped && playersIn === 2, skipped });
    if (keepsLength) skipsOwed += skipped ? -1 : out - 1;
    playersIn -= out;
  }
  if (recordedOut.length > plan.length) {
    throw new RangeError(
      `Battle Royale recorded ${recordedOut.length} eliminations, the game was decided at elimination ${plan.length}`,
    );
  }
  return plan;
}

/** End of a full game where everyone trades: its last planned elimination. */
export function battleRoyaleFullGameEndMs(rule: BattleRoyaleRule, startMs: number, playerCount: number): number {
  return planBattleRoyaleGame(rule, { startMs, playerCount, idle: 0 }).at(-1)!.atMs;
}

/**
 * Longest a game can run from its start, by roster size: the latest planned end over every idle sweep at
 * elimination 1. After elimination 1 a re-planned game depends only on the players left, so that rest is memoized.
 */
function longestGameMsBySize(rule: BattleRoyaleRule): (playerCount: number) => number {
  const warmUpMs = battleRoyaleWarmUpSeconds(rule) * 1000;
  const restMsByPlayersLeft = new Map<number, number>();
  const restMs = (playersLeft: number): number => {
    const known = restMsByPlayersLeft.get(playersLeft);
    if (known !== undefined) return known;
    let ms = 0;
    let playersIn = playersLeft;
    for (const out of planBattleRoyaleCuts(rule, playersLeft, 2)) {
      ms += runUpSeconds(rule, 1, playersIn) * 1000;
      playersIn -= out;
    }
    restMsByPlayersLeft.set(playersLeft, ms);
    return ms;
  };
  return (playerCount) => {
    // A sweep only skips eliminations or decides the game at elimination 1, never moves the full game's end.
    if (battleRoyaleKeepsPlannedLength(rule)) return warmUpMs + restMs(playerCount - 1);
    let longestMs = 0;
    for (let idle = 0; idle <= playerCount; idle++) {
      longestMs = Math.max(longestMs, warmUpMs + restMs(playerCount - firstEliminationOut(rule, playerCount, idle)));
    }
    return longestMs;
  };
}

/** Longest a game of exactly `playerCount` can run from its start, whoever is idle at elimination 1. */
export function battleRoyaleRosterLongestGameMs(rule: BattleRoyaleRule, playerCount: number): number {
  assertBattleRoyalePlayerCount(playerCount);
  return longestGameMsBySize(rule)(playerCount);
}

/**
 * Longest a game of up to `maxPlayers` can run from its start: every room size and every idle sweep.
 * A smaller room can run longer than a full one, e.g. when a sweep leaves a 1v1 with a long final.
 */
export function battleRoyaleLongestGameMs(rule: BattleRoyaleRule, maxPlayers: number): number {
  assertBattleRoyalePlayerCount(maxPlayers);
  const longestFor = longestGameMsBySize(rule);
  let longestMs = 0;
  for (let players = Math.min(BATTLE_ROYALE_MIN_PLAYERS, maxPlayers); players <= maxPlayers; players++) {
    longestMs = Math.max(longestMs, longestFor(players));
  }
  return longestMs;
}

/** The epoch end to store for up to `maxPlayers` players, grace included; it may only shrink once the roster freezes. */
export const battleRoyaleStoredEndMs = (rule: BattleRoyaleRule, startMs: number, maxPlayers: number): number =>
  startMs + battleRoyaleLongestGameMs(rule, maxPlayers) + BATTLE_ROYALE_END_GRACE_MS;

export type BattleRoyaleRoster =
  | { kind: "OK"; hubUserIds: number[] }
  | { kind: "EMPTY" }
  // A legacy wallet-string entry names a player no paper account or nickname maps to.
  | { kind: "NON_HUB_USER_ID_ENTRY" };

// The frozen roster is the campaign's authorizedParticipants; it is usable only as a non-empty list of hubUserIds.
export const parseBattleRoyaleRoster = (authorizedParticipants: ReadonlyArray<number | string>): BattleRoyaleRoster => {
  if (authorizedParticipants.length === 0) return { kind: "EMPTY" };
  if (!authorizedParticipants.every((entry): entry is number => typeof entry === "number")) {
    return { kind: "NON_HUB_USER_ID_ENTRY" };
  }
  return { kind: "OK", hubUserIds: [...authorizedParticipants] };
};

// A Battle Royale pays by elimination order, so it never carries a Spin & Go multiplier.
export const isBattleRoyaleSpinAndGo = (format: { isBattleRoyale: boolean; isSpinAndGo: boolean }): boolean =>
  format.isBattleRoyale && format.isSpinAndGo;

export const BattleRoyaleStatePlayerSchema = z.object({
  hubUserId: z.number().int(),
  place: z.object({ from: z.number().int().positive(), to: z.number().int().positive() }),
  pnlUsd: z.string().nullable(), // null: never traded
});
export const BattleRoyaleStateEliminationSchema = z.object({
  index: z.number().int().positive(),
  executedAt: z.coerce.date(),
  // ONE mode: consumed by an earlier elimination that put several out, so nobody went out.
  skipped: z.boolean(),
  players: z.array(BattleRoyaleStatePlayerSchema),
});
export const BattleRoyaleStateDtoSchema = z.object({
  campaignName: z.string(),
  eliminationIntervalSeconds: z.number().int().positive(),
  // Defaults read an API still on the one-per-elimination contract the same way as a stored config.
  firstEliminationSeconds: z.number().int().positive().nullable().default(null),
  finalEliminationSeconds: z.number().int().positive().nullable().default(null),
  elimination: BattleRoyaleEliminationRuleReadSchema.default(BATTLE_ROYALE_ONE_PER_ROUND),
  finalDuel: z.boolean().default(true),
  startsAt: z.coerce.date(),
  // Before elimination 1 the latest this roster can end, whoever is idle; after it, the planned end.
  endsAt: z.coerce.date(),
  playerCount: z.number().int().nonnegative(),
  // Full frozen roster, including players who never traded.
  roster: z.array(z.object({ hubUserId: z.number().int(), nickname: z.string().nullable() })),
  eliminations: z.array(BattleRoyaleStateEliminationSchema),
});
export type BattleRoyaleStateDto = z.infer<typeof BattleRoyaleStateDtoSchema>;

// Error codes of `GET /v1/cede-arena/battle-royale/:campaignName` a client branches on; not a Battle Royale is the plain campaign 404.
export const BATTLE_ROYALE_STATE_ERROR_CODE = {
  ROSTER_NOT_FROZEN: "TRADE_TO_EARN_ARENA_BATTLE_ROYALE_ROSTER_NOT_FROZEN", // 409: scheduled, not started yet
} as const;
